import {
  SNAP_SESSION_ID,
  SNAP_LAST_TIMESTAMP,
  SNAP_VISIT_SESSION_INTERVAL,
  SNAP_USER_EMAIL,
  SNAP_USER_EMAIL_TS,
  SNAP_EMAIL_HOLD_INTERVAL,
} from "../constants";

const mintId = (): string =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

/**
 * The visit session_id (FOC-136) — a client-minted UUID persisted in
 * localStorage and shared across every scan in a visit. It resets to a new
 * UUID when the last scan was more than SNAP_VISIT_SESSION_INTERVAL (2h) ago,
 * so a genuinely new visit gets a new session_id (per Steve: "resets after two
 * hours from last scan").
 *
 * Client-minted on purpose: it is NOT the server express-session id, which is
 * backed by the RDS the strangler epic (FOC-138/FOC-110) is decommissioning.
 * Consumed by the recap-email pipeline (grouping + the once-only email ingest),
 * the exactly-once sent-flag, and query-frame retention.
 *
 * Call at the START of a scan session, BEFORE stamping the new last-scan
 * timestamp, so the 2h gap is measured against the PREVIOUS scan.
 */
export const getVisitSessionId = (): string => {
  try {
    const existing = localStorage.getItem(SNAP_SESSION_ID);
    const lastScan = parseInt(
      localStorage.getItem(SNAP_LAST_TIMESTAMP) || "",
      10
    );
    const stale =
      Number.isNaN(lastScan) ||
      Date.now() - lastScan > SNAP_VISIT_SESSION_INTERVAL;

    if (existing && !stale) return existing;

    const fresh = mintId();
    localStorage.setItem(SNAP_SESSION_ID, fresh);
    return fresh;
  } catch {
    // localStorage unavailable (private mode, etc.) — still hand back an id so
    // the scan carries a session marker, just not a persisted one.
    return mintId();
  }
};

/** The client's last-scan timestamp in ms, or undefined if none recorded. */
export const getLastScanTs = (): number | undefined => {
  try {
    const v = parseInt(localStorage.getItem(SNAP_LAST_TIMESTAMP) || "", 10);
    return Number.isNaN(v) ? undefined : v;
  } catch {
    return undefined;
  }
};

/**
 * The locally-held email (FOC-143 2/3), only if within the 24h hold window —
 * else null (expired or absent). Drives the re-submit-on-new-session and,
 * once expired, lets the email tab reappear for re-entry. Does not clear the
 * stored key; the existing 24h app-reset handles cleanup.
 */
export const getHeldEmail = (): string | null => {
  try {
    const email = localStorage.getItem(SNAP_USER_EMAIL);
    if (!email) return null;
    // The 24h hold rolls forward from the LAST SCAN, not the email-entry time
    // (Steve): a visitor who returns 23h after their last scan — even 25h after
    // first entering their email — is still an active visit, so keep the email;
    // it expires only after 24h with no scan. Fall back to the entry timestamp
    // if for some reason no scan timestamp is recorded.
    const lastScan = getLastScanTs();
    const entryTs = parseInt(
      localStorage.getItem(SNAP_USER_EMAIL_TS) || "",
      10
    );
    const basis = lastScan ?? (Number.isNaN(entryTs) ? undefined : entryTs);
    if (basis === undefined || Date.now() - basis > SNAP_EMAIL_HOLD_INTERVAL) {
      return null; // no scan within the 24h hold — expired, don't re-submit
    }
    return email;
  } catch {
    return null;
  }
};
